/**
 * TabBar: underlined tabs that switch the view below them (Logs tabs, Batch results/URLs).
 *
 *   <TabBar
 *     label="Log type"
 *     value={tab}
 *     onChange={setTab}
 *     tabs={[{ value: "search", label: "Search" }, { value: "scrape", label: "Scrape", count: 47 }]}
 *     idPrefix="logs"                      tab ids are `${idPrefix}-tab-${value}`; give the panel
 *   />                                     id `${idPrefix}-panel` and aria-labelledby the active tab id
 *
 * The bar scrolls sideways inside itself when the tabs don't fit (phones), with a fade on the cut-off side, and the
 * active tab is scrolled into view. Keyboard: arrows, Home and End move between tabs (automatic activation).
 */
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { formatNumber } from "@/lib/format";

export interface TabItem<T extends string> {
  value: T;
  label: string;
  count?: number;
}

interface TabBarProps<T extends string> {
  tabs: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  idPrefix: string;
  className?: string;
}

export default function TabBar<T extends string>({ tabs, value, onChange, label, idPrefix, className = "" }: TabBarProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const measure = () => {
    const el = listRef.current;
    if (!el) return;
    setFade({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  };

  useEffect(() => {
    measure();
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    active?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [value]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex(t => t.value === value);
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(tabs[next].value);
    requestAnimationFrame(() => listRef.current?.querySelector<HTMLElement>(`#${idPrefix}-tab-${tabs[next].value}`)?.focus());
  };

  return (
    <div className={`relative border-b border-line ${className}`}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        onScroll={measure}
        className="-mb-px flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {tabs.map(t => {
          const selected = t.value === value;
          return (
            <button
              key={t.value}
              id={`${idPrefix}-tab-${t.value}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${idPrefix}-panel`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(t.value)}
              className={`shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ${
                selected ? "border-primary text-primary-text" : "border-transparent text-fg-muted hover:text-fg"
              }`}
            >
              {t.label}
              {t.count !== undefined && <span className="ml-1.5 text-fg-muted">{formatNumber(t.count)}</span>}
            </button>
          );
        })}
      </div>
      {fade.left && <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-canvas to-transparent" />}
      {fade.right && <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-canvas to-transparent" />}
    </div>
  );
}
